import ResearchItemCard from "./ResearchItemCard";
import {
  ArticleRounded as FilingIcon,
  SearchRounded as SearchIcon,
  SmartToyRounded as AiIcon,
} from "@mui/icons-material";
import { Box, Paper, Stack, Typography } from "@mui/material";
import BigLoader from "components/common/BigLoader";

const EMPTY_MODES = [
  {
    icon: SearchIcon,
    label: "Web search",
    body: "Find news, lawsuits, regulatory actions for the named insured.",
    color: "primary.main",
  },
  {
    icon: AiIcon,
    label: "AI research",
    body:
      "Synthesised answers using web results, optionally with submission context and SEC filings.",
    color: "secondary.main",
  },
  {
    icon: FilingIcon,
    label: "SEC filing",
    body:
      "Pull the latest 10-K and 10-Q for any public company by ticker, name, or CIK.",
    color: "info.main",
  },
];

const EmptyState = () => (
  <Paper
    elevation={0}
    sx={{ p: 2, bgcolor: "background.default", borderRadius: 1 }}
  >
    <Typography
      variant={"caption"}
      color={"text.secondary"}
      fontWeight={600}
      sx={{ display: "block", mb: 1 }}
    >
      {"No research yet — pick a mode above"}
    </Typography>
    <Stack spacing={1}>
      {EMPTY_MODES.map(({ icon: Icon, label, body, color }) => (
        <Stack
          key={label}
          direction={"row"}
          spacing={1}
          alignItems={"flex-start"}
        >
          <Box
            sx={{
              p: 0.5,
              borderRadius: 0.5,
              bgcolor: "action.hover",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 16, color }} />
          </Box>
          <Stack spacing={0.25} sx={{ minWidth: 0 }}>
            <Typography
              variant={"caption"}
              fontWeight={600}
              sx={{ fontSize: "0.75rem" }}
            >
              {label}
            </Typography>
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ fontSize: "0.7rem", lineHeight: 1.4 }}
            >
              {body}
            </Typography>
          </Stack>
        </Stack>
      ))}
    </Stack>
  </Paper>
);

const ResearchTimeline = ({
  items,
  isLoading,
  onUpdateItem,
  onCiteSource,
  onDeleteItem,
  onSaveAttachment,
  onFollowUp,
  updatingItemId,
}) => {
  if (isLoading) {
    return <BigLoader />;
  }

  if (!items || items.length === 0) {
    return <EmptyState />;
  }

  const sortedItems = [...items].sort((a, b) => {
    const dateA = a.createdAt ? new Date(a.createdAt) : 0;
    const dateB = b.createdAt ? new Date(b.createdAt) : 0;
    return dateB - dateA;
  });

  return (
    <Stack spacing={1}>
      {sortedItems.map((item) => (
        <ResearchItemCard
          key={item.itemId}
          item={item}
          onUpdate={(updates) => onUpdateItem(item.itemId, updates)}
          onCiteSource={(sourceUrl, cited, citedIn) =>
            onCiteSource(item.itemId, sourceUrl, cited, citedIn)
          }
          onDelete={() => onDeleteItem(item.itemId)}
          onSaveAttachment={
            onSaveAttachment ? () => onSaveAttachment(item.itemId) : undefined
          }
          onFollowUp={
            onFollowUp
              ? (query, options) => onFollowUp(item.itemId, query, options)
              : undefined
          }
          isUpdating={updatingItemId === item.itemId}
        />
      ))}
    </Stack>
  );
};

export default ResearchTimeline;
